import type { PaymentHistoryResponse } from '../../types/member.js';
import { useTranslation } from 'react-i18next';

interface PaymentTabProps {
  data: PaymentHistoryResponse;
}

/** @graph-connects none */
export function PaymentTab({ data }: PaymentTabProps) {
  const { t } = useTranslation();

  if (data.payments.length === 0) {
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] py-12 text-center text-sm text-[var(--color-text-muted)]">
        {t('member.noPaymentHistory')}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="border-b border-[var(--color-border)] px-4 py-3 text-[13px] font-semibold text-[var(--color-text-primary)]">
        {t('member.paymentHistory')}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.paymentDate')}
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.paymentItem')}
              </th>
              <th className="px-3.5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.amountTaxIncluded')}
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.purchasedSku')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.payments.map((payment, i) => (
              <tr key={i} className="border-b border-[var(--color-border)]">
                <td className="whitespace-nowrap px-3.5 py-2.5 font-mono text-[12px]">
                  {formatDate(payment.purchaseDate)}
                </td>
                <td className="whitespace-nowrap px-3.5 py-2.5 text-[13px]">
                  {payment.paymentType}
                </td>
                <td className="whitespace-nowrap px-3.5 py-2.5 text-right font-mono text-[12px]">
                  ¥{formatNumber(payment.amount)}
                </td>
                <td className="px-3.5 py-2.5">
                  {payment.purchasedSkus.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {payment.purchasedSkus.map((sku) => (
                        <div key={sku.skuId} className="flex flex-col items-center gap-1">
                          {sku.imageUrl ? (
                            <img
                              src={sku.imageUrl}
                              alt={`SKU ${String(sku.skuId)}`}
                              className="h-[48px] w-[38px] rounded border border-[var(--color-border)] object-cover"
                            />
                          ) : (
                            <div className="h-[48px] w-[38px] rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)]" />
                          )}
                          <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                            {String(sku.skuId)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[12px] text-[var(--color-text-muted)]">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @graph-connects none */
function formatDate(isoDate: string): string {
  const _d = new Date(isoDate);
  if (Number.isNaN(_d.getTime())) return isoDate;
  return _d.toLocaleDateString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/** @graph-connects none */
function formatNumber(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
